/**
 * Criterios de demanda → query de GET /leads/propiedades/buscar
 * (misma lógica que FichaCliente.cargarProps).
 */
import { parsePrecioVentaEntero, formatPrecioVentaEntero } from "./moneyParse";
import { dedupePoblacionNombres } from "./poblacionesFavoritas";
import { joinPoblacionesQueryParam } from "./poblacionesQueryParam";

function listaDesdeCampoDemanda(val) {
  if (val == null || val === "") return [];
  if (Array.isArray(val)) return val.map(String).map((s) => s.trim()).filter(Boolean);
  const s = String(val).trim();
  if (!s) return [];
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}

function precioCriterioStr(raw) {
  if (raw == null || raw === "") return "";
  const n = parsePrecioVentaEntero(raw);
  return Number.isFinite(n) ? formatPrecioVentaEntero(n) : String(raw);
}

function precioCriterioNum(raw) {
  if (raw == null || raw === "") return null;
  const n = parsePrecioVentaEntero(raw);
  return Number.isFinite(n) ? n : null;
}

function scoreDemandaFicha(d = {}) {
  let s = 0;
  if (Array.isArray(d.tipos) && d.tipos.length) s += 3;
  if (d.poblacion || (Array.isArray(d.poblaciones) && d.poblaciones.length)) s += 3;
  if (d.precio_min != null || d.precio_max != null) s += 2;
  if (d.habitaciones_min != null) s += 1;
  if (d.geo_centro_lat != null && d.geo_centro_lng != null) s += 2;
  return s;
}

function esDemandaCancelada(d) {
  const e = String(d?.estado || "").trim().toLowerCase();
  return e === "cancelada" || e === "cancelado" || e === "cerrada" || e === "cerrado" || e === "inactiva";
}

/** Demandas activas (no canceladas/cerradas), orden ficha: updated_at + score. */
export function listarDemandasActivas(demandas = []) {
  if (!Array.isArray(demandas) || !demandas.length) return [];
  return [...demandas]
    .filter((d) => d && d.activo !== false && !esDemandaCancelada(d))
    .sort((a, b) => {
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      if (tb !== ta) return tb - ta;
      return (
        scoreDemandaFicha(b) - scoreDemandaFicha(a)
        || new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    });
}

/**
 * Misma lógica que FichaCliente.pickDemandaActiva:
 * no canceladas primero, luego updated_at, luego score de criterios.
 */
export function pickDemandaActiva(demandas = []) {
  const activas = listarDemandasActivas(demandas);
  if (activas.length) return activas[0];
  if (!Array.isArray(demandas) || !demandas.length) return null;
  const sorted = [...demandas].filter((d) => d && d.activo !== false).sort((a, b) => {
    const ca = esDemandaCancelada(a) ? 1 : 0;
    const cb = esDemandaCancelada(b) ? 1 : 0;
    if (ca !== cb) return ca - cb;
    const tb = new Date(b.updated_at || b.created_at || 0).getTime();
    const ta = new Date(a.updated_at || a.created_at || 0).getTime();
    if (tb !== ta) return tb - ta;
    return (
      scoreDemandaFicha(b) - scoreDemandaFicha(a)
      || new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
  });
  return sorted[0] || null;
}

export function extraerCriteriosDesdeDemanda(dm, operacionFallback) {
  if (!dm || typeof dm !== "object") return null;
  const extras = [];
  if (dm.piscina) extras.push("piscina");
  if (dm.garaje) extras.push("garaje");
  if (dm.terraza) extras.push("terraza");
  if (dm.ascensor) extras.push("ascensor");
  if (dm.amueblado) extras.push("amueblado");
  if (dm.primera_linea) extras.push("primera_linea");
  if (dm.vista_mar) extras.push("vista_mar");
  const extrasExcl = [];
  if (dm.piscina_excl === true) extrasExcl.push("piscina");
  if (dm.amueblado_excl === true) extrasExcl.push("amueblado");

  return {
    tipos: dm.tipos || [],
    op: dm.operacion || operacionFallback || "",
    pMin: dm.precio_min != null ? precioCriterioStr(dm.precio_min) : "",
    pMax: dm.precio_max != null ? precioCriterioStr(dm.precio_max) : "",
    hMin: dm.habitaciones_min != null ? String(dm.habitaciones_min) : "",
    bMin: dm.banos_min != null ? String(dm.banos_min) : "",
    m2Min: dm.m2_min != null ? String(dm.m2_min) : "",
    m2Max: dm.m2_max != null ? String(dm.m2_max) : "",
    pobls: (() => {
      const fromArr = listaDesdeCampoDemanda(dm.poblaciones);
      const raw = fromArr.length ? fromArr : listaDesdeCampoDemanda(dm.poblacion);
      return dedupePoblacionNombres(raw);
    })(),
    zonas: (() => {
      const fromArr = listaDesdeCampoDemanda(dm.zonas);
      if (fromArr.length) return fromArr;
      return listaDesdeCampoDemanda(dm.zona);
    })(),
    zonasExcl: listaDesdeCampoDemanda(dm.zonas_excl),
    extras,
    extrasExcl,
    equip: [],
    sinAsc: false,
    plMax: 2,
    m2Parcela: dm.m2_parcela_min != null ? String(dm.m2_parcela_min) : "",
    orientacion: dm.orientacion || [],
    orientacionExcl: listaDesdeCampoDemanda(dm.orientaciones_excl),
    jardin: dm.jardin || false,
    anoConstruccion: dm.ano_construccion_desde != null ? String(dm.ano_construccion_desde) : "",
    estadoInmueble: dm.estado_inmueble || [],
    estadoInmuebleExcl: listaDesdeCampoDemanda(dm.estado_inmueble_excl),
    vpo: dm.vpo || false,
    vpoExcl: dm.vpo_excl === true,
    calEnergetica: dm.cal_energetica || "",
    plantas: dm.plantas || [],
    plantasExcl: listaDesdeCampoDemanda(dm.plantas_excl),
    aireAc: dm.aire_acondicionado || false,
    calefaccion: dm.calefaccion || false,
    padel: dm.pista_padel || false,
    padelExcl: dm.pista_padel_excl === true,
    tenis: dm.pista_tenis || false,
    tenisExcl: dm.pista_tenis_excl === true,
    puntoRecarga: dm.punto_recarga || false,
    adaptado: dm.adaptado || false,
    salidaHumos: dm.salida_humos || false,
    distanciaMar: dm.distancia_mar_max != null ? String(dm.distancia_mar_max) : "",
    urbanizacionId: dm.urbanizacion_id || "",
    geoLat: dm.geo_centro_lat != null ? Number(dm.geo_centro_lat) : null,
    geoLng: dm.geo_centro_lng != null ? Number(dm.geo_centro_lng) : null,
    geoRadioM: dm.geo_radio_m != null ? Number(dm.geo_radio_m) : null,
  };
}

/** URLSearchParams para GET /leads/propiedades/buscar */
export function buildBuscarPropiedadesQuery(criterios, clienteId) {
  const q = new URLSearchParams();
  if (!criterios) return q;
  if (clienteId) q.set("cliente_id", String(clienteId));
  if (criterios.tipos?.length > 0) q.set("tipos", criterios.tipos.join(","));
  if (criterios.op) q.set("operacion", criterios.op);
  const pMinApi = precioCriterioNum(criterios.pMin);
  const pMaxApi = precioCriterioNum(criterios.pMax);
  if (pMinApi != null) q.set("precio_min", String(pMinApi));
  if (pMaxApi != null) q.set("precio_max", String(pMaxApi));
  if (criterios.hMin) q.set("habs_min", criterios.hMin);
  if (criterios.bMin) q.set("banos_min", criterios.bMin);
  if (criterios.m2Min) q.set("m2_min", criterios.m2Min);
  if (criterios.m2Max) q.set("m2_max", criterios.m2Max);
  if (criterios.pobls?.length > 0) q.set("poblaciones", joinPoblacionesQueryParam(criterios.pobls));
  if (criterios.zonas?.length > 0) q.set("zonas", criterios.zonas.join(","));
  if (criterios.zonasExcl?.length > 0) q.set("zonas_excluidas", criterios.zonasExcl.join(","));
  if (
    criterios.geoLat != null
    && criterios.geoLng != null
    && criterios.geoRadioM != null
    && Number(criterios.geoRadioM) > 0
  ) {
    q.set("geo_lat", String(criterios.geoLat));
    q.set("geo_lng", String(criterios.geoLng));
    q.set("geo_radio_m", String(Math.round(criterios.geoRadioM)));
  }
  if (criterios.extras?.includes("piscina")) q.set("piscina", "true");
  if (criterios.extras?.includes("garaje")) q.set("garaje", "true");
  if (criterios.extras?.includes("terraza")) q.set("terraza", "true");
  if (criterios.extras?.includes("ascensor")) q.set("ascensor", "true");
  if (criterios.extras?.includes("amueblado")) q.set("amueblado", "true");
  if (criterios.extras?.includes("primera_linea")) q.set("primera_linea", "true");
  if (criterios.extras?.includes("vista_mar")) q.set("vista_mar", "true");
  if (criterios.extrasExcl?.includes("piscina")) q.set("piscina_excl", "true");
  if (criterios.extrasExcl?.includes("amueblado")) q.set("amueblado_excl", "true");
  if (criterios.m2Parcela) q.set("m2_parcela_min", criterios.m2Parcela);
  if (criterios.orientacion?.length > 0) q.set("orientaciones", criterios.orientacion.join(","));
  if (criterios.orientacionExcl?.length > 0) q.set("orientaciones_excl", criterios.orientacionExcl.join(","));
  if (criterios.jardin) q.set("jardin", "true");
  if (criterios.anoConstruccion) q.set("ano_construccion_desde", criterios.anoConstruccion);
  if (criterios.estadoInmueble?.length > 0) q.set("estado_inmueble", criterios.estadoInmueble.join(","));
  if (criterios.estadoInmuebleExcl?.length > 0) {
    q.set("estado_inmueble_excl", criterios.estadoInmuebleExcl.join(","));
  }
  if (criterios.vpo) q.set("vpo", "true");
  if (criterios.vpoExcl) q.set("vpo_excl", "true");
  if (criterios.calEnergetica) q.set("cal_energetica", criterios.calEnergetica);
  if (criterios.plantas?.length > 0) q.set("plantas", criterios.plantas.join(","));
  if (criterios.plantasExcl?.length > 0) q.set("plantas_excl", criterios.plantasExcl.join(","));
  if (criterios.aireAc) q.set("aire_acondicionado", "true");
  if (criterios.calefaccion) q.set("calefaccion", "true");
  if (criterios.padel) q.set("padel", "true");
  if (criterios.padelExcl) q.set("padel_excl", "true");
  if (criterios.tenis) q.set("tenis", "true");
  if (criterios.tenisExcl) q.set("tenis_excl", "true");
  if (criterios.puntoRecarga) q.set("punto_recarga", "true");
  if (criterios.adaptado) q.set("adaptado", "true");
  if (criterios.salidaHumos) q.set("salida_humos", "true");
  if (criterios.distanciaMar) q.set("distancia_mar_max", criterios.distanciaMar);
  if (criterios.urbanizacionId) q.set("urbanizacion_id", criterios.urbanizacionId);
  if (criterios.sinAsc) q.set("sin_asc_planta", criterios.plMax);
  if (criterios.equip?.length > 0) q.set("equip", criterios.equip.join(","));
  return q;
}
